/**
 * LocalDataMixin の format_*（rirekisho 等）経路。
 * basicData 専用の useLocalFormatData とは別。mixin 本体は他消費者のため残置。
 *
 * format-data-changed 等は mitt 正本。未移行の hub 購読は emitter.js の mitt→hub forward。
 */
import localforage from 'localforage'
import {
  getCurrentInstance,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  reactive,
  ref,
  watch,
  type Ref
} from 'vue'
import { useDebounceFn } from '@vueuse/core'
import { fetchBlobUrlAsBase64 } from '@/mixins/blob-utils'
import { emitter } from '@/plugins/emitter'

const SAVE_KEY_PREFIX = 'format_'
const SAVE_INTERVAL_MS = 1000
const FORMAT_DATA_CHANGED = 'format-data-changed'
const FORMAT_OTHERDATA_CHANGED = 'format-otherdata-changed'

type YagiHost = {
  $yagi?: { profile?: { autoSaveEnabled?: boolean } }
}

export type FormatLocalDataOptions<T extends Record<string, unknown>> = {
  saveName: string
  saveOtherName?: string
  createEmpty: () => T
  /** ロード済みデータに適用するマイグレーション関数（古い形式の値を補正する） */
  migrate?: (data: T) => T
}

export function useFormatLocalData<T extends Record<string, unknown>>(
  options: FormatLocalDataOptions<T>
): {
  formatData: T
  otherFormatData: Ref<Record<string, unknown>>
  isNowSaving: Ref<boolean>
  saveKey: string
  saveName: string
  changeCount: Ref<number>
} {
  const { saveName, saveOtherName = '', createEmpty, migrate } = options
  const saveKey = SAVE_KEY_PREFIX + saveName
  const saveOtherKey = saveOtherName ? SAVE_KEY_PREFIX + saveOtherName : ''
  const backupLoadedEvent = 'backup-loaded-' + saveKey

  const formatData = reactive(createEmpty()) as T
  const otherFormatData = ref<Record<string, unknown>>({})
  const isNowSaving = ref(false)
  const changeCount = ref(0)
  const vm = getCurrentInstance()!.proxy as YagiHost

  function applyLoadedData(data: T | null | undefined) {
    if (data == null) {
      emitter.emit('local-format-data-load-finished', saveName)
      return
    }
    const migratedData = migrate ? migrate(data) : data
    Object.assign(formatData, migratedData)
    // migrate で変換した場合は localforage に即時書き戻す。
    // addBackup は $iterateStorage で localforage を直接読むため、
    // debounce 保存を待つと旧値が送信されてしまう。
    if (migrate) {
      localforage
        .setItem(saveKey, Object.assign({}, migratedData))
        .catch((e) => {
          console.warn('[useFormatLocalData] migrate後の書き戻し失敗:', e)
        })
    }
    nextTick(() => {
      emitter.emit('local-format-data-loaded', saveName)
      emitter.emit('local-format-data-load-finished', saveName)
    })
    changeCount.value = 0
  }

  async function loadFormatData() {
    try {
      const data = (await localforage.getItem(saveKey)) as T | null
      applyLoadedData(data)
    } catch (err) {
      console.log(err)
      emitter.emit('local-format-data-load-finished', saveName)
    }
  }

  async function loadOtherFormatData() {
    if (!saveOtherKey) return
    try {
      const data = await localforage.getItem(saveOtherKey)
      if (data == null) return
      otherFormatData.value = data as Record<string, unknown>
    } catch (err) {
      console.log(err)
    }
  }

  const saveFormatData = useDebounceFn(async () => {
    if (!saveKey) return
    isNowSaving.value = true
    try {
      const dataToSave = Object.assign({}, formatData) as T & {
        idPhotoSrc?: string
      }
      if (
        dataToSave.idPhotoSrc &&
        String(dataToSave.idPhotoSrc).startsWith('blob:')
      ) {
        try {
          dataToSave.idPhotoSrc = (await fetchBlobUrlAsBase64(
            dataToSave.idPhotoSrc
          )) as string
        } catch (e) {
          console.warn('[useFormatLocalData] blob URL→base64変換失敗:', e)
          isNowSaving.value = false
          return
        }
      }
      await localforage.setItem(saveKey, dataToSave)
      emitter.emit('save-complete-notify', 'おぼえた')
      emitter.emit('yagi-message', { text: 'おぼえた' })
    } finally {
      isNowSaving.value = false
    }
  }, SAVE_INTERVAL_MS)

  watch(
    formatData,
    (val) => {
      for (const rval in val) {
        const tval = (val as Record<string, unknown>)[rval]
        if (typeof tval === 'string') {
          ;(val as Record<string, string>)[rval] = tval.replace('\u0000', '')
        }
      }
      emitter.emit(FORMAT_DATA_CHANGED, val)
      changeCount.value++
    },
    { deep: true }
  )

  watch(
    otherFormatData,
    (val) => {
      emitter.emit(FORMAT_OTHERDATA_CHANGED, val)
    },
    { deep: true }
  )

  watch(changeCount, () => {
    if (!vm.$yagi?.profile?.autoSaveEnabled) {
      console.log(
        'saveFormatData() skipped autoSaveEnabled is false',
        vm.$yagi?.profile?.autoSaveEnabled
      )
      return
    }
    if (changeCount.value < 2) return
    if (!isNowSaving.value) {
      saveFormatData()
    } else {
      console.log('skip saveFormatData', isNowSaving.value)
    }
  })

  onBeforeMount(() => {
    loadFormatData()
    loadOtherFormatData()
    emitter.on(backupLoadedEvent, loadFormatData)
  })

  onBeforeUnmount(() => {
    emitter.off(backupLoadedEvent, loadFormatData)
  })

  return {
    formatData,
    otherFormatData,
    isNowSaving,
    saveKey,
    saveName,
    changeCount
  }
}
